import { z } from 'zod';

import { optionalEnum } from '../../../utils.js';
import { GetEncounterMode } from '../../encounter.js';
import { FeedMessageFormat } from '../../feedMessage.js';
import { GetOrderOption } from '../../order.js';
import { PatientMatchStrictness } from '../../patient.js';
import * as PatientUpload from '../../patientUpload.js';
import { GetJobContextMode, Kind as RpaJobKind, RpaJobSuccessFilter } from '../../rpaJob.js';
import {
  BaseFlowBlockSchema,
  BaseNotableObjectConfigSchema,
  BlockType,
  ConsumerOfPatientSchema,
  NotableObject,
} from '../base/baseSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';
import { ProviderIDType } from '../getProvider/config.js';
import { FilterConfigSchema } from '../utils/filterConfig.js';

export const MAX_JOB_CONTEXT_LIMIT = 25;

export const GetHl7MessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Hl7MessageEvent),
});

export const GetGuarantorConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Guarantor),
  patient: ConsumerOfPatientSchema({ isRequired: true }),
});

export const GetPaymentConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Payment),
});

export const GetAgentConversationConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.AgentConversation),
});

export const GetReferenceTableConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.ReferenceTable),
  referenceTableId: z.uuid(),
  selectedColumns: z.array(z.string()).optional(),
  filterConfig: FilterConfigSchema.optional(),
});

export const GetPatientOrderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientOrder),
  patientOrderId: z.string().optional(),
  patient: ConsumerOfPatientSchema({ isRequired: false }),
  getOrderOption: optionalEnum(GetOrderOption, GetOrderOption.GetOneById),
});

export const GetProviderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Provider),
  providerIdType: z.enum(ProviderIDType).optional(),
  isSelectMultipleProviderIdsEnabled: z.boolean().default(false),
  providerId: z.string().optional(),
});

export enum GetExternalDocumentPacketOption {
  GetOneById = 'GetOneById',
  FindByFileName = 'FindByFileName',
}

export const GetTemplateConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Template),
  templateId: z.string(),
  ehrInformationId: z.string(),
});

const GetExternalDocumentPacketBaseSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.ExternalDocumentPacket),
});

export const GetExternalDocumentPacketConfigSchema = GetExternalDocumentPacketBaseSchema.extend({
  getExternalDocumentPacketOption: z
    .enum(GetExternalDocumentPacketOption)
    .default(GetExternalDocumentPacketOption.GetOneById),
  externalDocumentPacketId: z.string().optional(),
  fileName: z.string().optional(),
});

export const GetEncountersConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Encounter),
  mode: z.enum(GetEncounterMode).default(GetEncounterMode.FindAll),
  patient: ConsumerOfPatientSchema({ isRequired: false }),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  encounterId: z.string().optional(),
});

export const GetPatientConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Patient),
  ehrInformationId: z.string().optional(),
  lastName: z.string().optional(),
  firstName: z.string().optional(),
  mrn: z.string().optional(),
  phoneNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  patientMatchStrictness: z
    .enum(PatientMatchStrictness)
    .optional()
    .default(PatientMatchStrictness.Strict),
});

export const GetPracticeConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Practice),
  isSelectPracticeIdsEnabled: z.boolean().default(false),
  practiceIds: z.string().optional(),
});

export const GetSftpMessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.SftpMessageEvent),
});

export const GetX12MessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.X12MessageEvent),
});

export const GetPatientUploadConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientUpload),
  documentType: z.enum(PatientUpload.DocumentType).optional(),
  patientInsuranceInformationId: z.string().optional(),
  previsitFormTemplateId: z.string().optional(),
});

export const GetPatientInsuranceInformationConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientInsuranceInformation),
});

export const GetPrimaryCareProviderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PrimaryCareProvider),
});

export const GetFeedMessagesConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.FeedMessages),
  source: z.string().optional(),
  limit: z.string().optional(),
  messageFormat: z.enum(FeedMessageFormat).optional(),
});

export const GetEncounterContactsConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.EncounterContacts),
  encounterId: z.string(),
});

export const GetInsuranceOcrResultsConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.InsuranceOcrResults),
  patientUploadId: z.string().min(1, { message: 'Patient upload ID is required' }),
});

export const GetJobContextConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.JobContext),
  mode: z.enum(GetJobContextMode).default(GetJobContextMode.FindAll),
  rpaJobId: z.string().optional(),
  jobKind: z.enum(RpaJobKind).optional(),
  successFilter: z.enum(RpaJobSuccessFilter).optional(),
  lookbackDuration: BlockValueTemplateDurationSchema().optional(),
  limit: z.string().optional(),
});

export type GetJobContextConfig = z.infer<typeof GetJobContextConfigSchema>;

export const GetAssistantConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Assistant),
  assistantId: z.string(),
});

export const GetNotableObjectConfigSchema = z.discriminatedUnion('notableObject', [
  GetReferenceTableConfigSchema,
  GetPaymentConfigSchema,
  GetAgentConversationConfigSchema,
  GetHl7MessageEventConfigSchema,
  GetGuarantorConfigSchema,
  GetPatientOrderConfigSchema,
  GetProviderConfigSchema,
  GetTemplateConfigSchema,
  GetExternalDocumentPacketConfigSchema,
  GetPatientConfigSchema,
  GetPracticeConfigSchema,
  GetSftpMessageEventConfigSchema,
  GetX12MessageEventConfigSchema,
  GetPatientUploadConfigSchema,
  GetPatientInsuranceInformationConfigSchema,
  GetEncountersConfigSchema,
  GetPrimaryCareProviderConfigSchema,
  GetFeedMessagesConfigSchema,
  GetEncounterContactsConfigSchema,
  GetInsuranceOcrResultsConfigSchema,
  GetJobContextConfigSchema,
  GetAssistantConfigSchema,
]);

export type GetNotableObjectConfig = z.infer<typeof GetNotableObjectConfigSchema>;

export const GetNotableObjectBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.GetNotableObject),
  config: GetNotableObjectConfigSchema,
});

export type GetNotableObjectBlock = z.infer<typeof GetNotableObjectBlockSchema>;
